import { FormFieldLabel } from '@alison/ui';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlined from '@mui/icons-material/KeyboardArrowUpOutlined';
import PriorityHighOutlined from '@mui/icons-material/PriorityHighOutlined';
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { type BrandGuidelineCheck, brandGuidelines } from './attributesAnalysis.js';
import { ScorePip } from './ScoreVisuals.js';

const PASS_FROM = 3;

const SUBTITLE =
  'How this creative aligns to your brand rules — this overrides the scored dimensions below.';

const EXPLANATION =
  'These elements ensure compliance with your brand guidelines. The brand guidelines score is an independent score.';

const CARD_SX = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const ROW_SX = {
  gap: 2,
  px: 2,
  py: 1.5,
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: 2,
  border: '1px solid transparent',
  bgcolor: 'background.page',
};

const FAILED_ROW_SX = { borderColor: 'score.low.main', bgcolor: 'background.paper' };

const FAILED_STRIP_SX = {
  gap: 1,
  px: 2,
  py: 1.5,
  alignItems: 'center',
  borderRadius: 2,
  bgcolor: 'warning.bg',
};

const GRID_SX = {
  display: 'grid',
  gap: 1,
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
};

export interface BrandGuidelinesSectionProps {
  result: unknown;
  creativeId: string;
  networkId: number;
}

function CheckRow({ check }: { check: BrandGuidelineCheck }): ReactNode {
  const passed = check.score === null ? null : check.score >= PASS_FROM;
  return (
    <Stack direction="row" sx={[ROW_SX, passed === false && FAILED_ROW_SX]}>
      <Stack sx={{ minWidth: 0 }}>
        <FormFieldLabel sx={{ fontSize: 9, color: 'text.muted' }}>
          {check.feature.toUpperCase()}
        </FormFieldLabel>
        <Typography sx={{ fontSize: 13 }}>{check.tag}</Typography>
      </Stack>
      {passed === null ? null : (
        <Stack
          direction="row"
          sx={{
            gap: 0.5,
            alignItems: 'center',
            color: passed ? 'score.high.main' : 'score.low.main',
          }}
        >
          {passed ? (
            <ThumbUpOutlined sx={{ fontSize: 14 }} />
          ) : (
            <ThumbDownOutlined sx={{ fontSize: 14 }} />
          )}
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{passed ? 'Pass' : 'Fail'}</Typography>
        </Stack>
      )}
    </Stack>
  );
}

export function BrandGuidelinesSection({
  result,
  creativeId,
  networkId,
}: BrandGuidelinesSectionProps): ReactNode {
  const [open, setOpen] = useState(true);
  const checks = brandGuidelines(result, creativeId, networkId);
  const total = checks.scored.length + checks.unscored.length;
  if (total === 0) return null;

  const failed = checks.scored.filter((check) => (check.score ?? 0) < PASS_FROM);

  return (
    <Stack data-testid="creative-brand-guidelines" sx={{ gap: 2 }}>
      <Stack
        direction="row"
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        sx={{
          gap: 2,
          p: 0,
          width: '100%',
          border: 0,
          bgcolor: 'transparent',
          cursor: 'pointer',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          textAlign: 'left',
        }}
      >
        <Box>
          <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
            <Typography variant="h3" sx={{ fontSize: 15 }}>
              Brand Guidelines
            </Typography>
            <Typography sx={{ color: 'text.muted' }}>Independent score</Typography>
            <Tooltip placement="top-start" title={EXPLANATION}>
              <InfoOutlined sx={{ fontSize: 16, color: 'text.muted' }} />
            </Tooltip>
          </Stack>
          <Typography sx={{ color: 'text.muted' }}>{SUBTITLE}</Typography>
        </Box>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <ScorePip score={checks.score} />
          <IconButton component="span" size="small" sx={{ color: 'text.muted' }}>
            {open ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        </Stack>
      </Stack>

      <Stack sx={CARD_SX}>
        <Collapse in={open}>
          <Stack sx={{ gap: 1, p: 2.5 }}>
            {failed.length === 0 ? null : (
              <Stack direction="row" sx={FAILED_STRIP_SX}>
                <PriorityHighOutlined sx={{ fontSize: 16, color: 'warning.main' }} />
                <Typography sx={{ fontSize: 13 }}>
                  {`${failed.length} check${failed.length === 1 ? '' : 's'} failed — ${failed
                    .map((check) => check.feature)
                    .join(', ')}. Brand rules take priority over the dimension scores.`}
                </Typography>
              </Stack>
            )}

            <Box sx={GRID_SX}>
              {checks.scored.map((check) => (
                <CheckRow key={`${check.feature}-${check.tag}`} check={check} />
              ))}
            </Box>

            {checks.unscored.length === 0 ? null : (
              <>
                <Typography sx={{ mt: 1, color: 'text.muted' }}>Tags without score</Typography>
                <Box sx={GRID_SX}>
                  {checks.unscored.map((check) => (
                    <CheckRow key={`${check.feature}-${check.tag}`} check={check} />
                  ))}
                </Box>
              </>
            )}
          </Stack>
        </Collapse>
      </Stack>
    </Stack>
  );
}
